import { useEffect, useRef, useState } from "react"
import { Head, Link } from "@inertiajs/react"
import { CheckoutLayout } from "@/components/CheckoutLayout"
import { CheckoutProgress } from "@/components/CheckoutProgress"
import { CopyField } from "@/components/ui/copy-field"
import { fetchJson } from "@/lib/fetch-json"

interface StatusResponse {
  state: "pending" | "ready"
  license_key?: string
  download_url?: string | null
}

export default function Complete() {
  const [status, setStatus] = useState<StatusResponse>({ state: "pending" })
  const [timedOut, setTimedOut] = useState(false)
  const attempts = useRef(0)

  useEffect(() => {
    let cancelled = false
    const poll = async () => {
      attempts.current += 1
      const result = await fetchJson<StatusResponse>("/checkout/status", { method: "GET" })
      if (cancelled) return
      if (result.ok && result.data.state === "ready") {
        setStatus(result.data)
      } else if (attempts.current >= 40) {
        setTimedOut(true)
      } else {
        window.setTimeout(poll, 1500)
      }
    }
    void poll()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <>
      <Head title="Purchase complete">
        <meta name="description" content="Your Flywrite purchase is complete." />
        <meta property="og:title" content="Purchase complete" />
        <meta property="og:description" content="Your Flywrite purchase is complete." />
      </Head>
      <CheckoutLayout
        mainHeader={<CheckoutProgress step={3} authenticated />}
        main={
          <>
          {status.state === "ready" ? (
            <>
              <h1>You're in 🎉</h1>
              <p className="mt-2 text-sm text-ink-body">
                Your license is ready. We've also emailed it to you, along with your download
                link.
              </p>

              <div className="mt-6 space-y-2">
                <label htmlFor="license-key">Your license key</label>
                <CopyField value={status.license_key ?? ""} ariaLabel="Copy license key" />
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                {status.download_url && (
                  <a href={status.download_url} className="btn btn-primary">
                    Download Flywrite for Mac
                  </a>
                )}
                <Link href="/account/license" className="btn btn-secondary">
                  Go to your account
                </Link>
              </div>

              <p className="mt-6 text-xs text-ink-muted">
                Open Flywrite, paste your license key when prompted, and you're writing. Manage
                seats, devices, and billing anytime from your account.
              </p>
            </>
          ) : timedOut ? (
            <>
              <h1>Almost there…</h1>
              <p className="mt-2 text-sm text-ink-body">
                Your payment is processing. This sometimes takes a minute — your license key
                will be emailed to you the moment it's ready, and it will appear on your{" "}
                <Link href="/account/license">License page</Link>.
              </p>
            </>
          ) : (
            <>
              <h1>Finalizing your purchase…</h1>
              <p className="mt-2 text-sm text-ink-muted">
                Hang tight — we're confirming your payment and issuing your license. This
                usually takes a few seconds.
              </p>
              <div className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-surface">
                <div className="h-full w-1/3 animate-pulse rounded-full bg-accent" />
              </div>
            </>
          )}
          </>
        }
      />
    </>
  )
}
